import { useTranslation } from 'react-i18next';
import {
  TicketStatusGroup,
  ticketPriorityLabelKey,
  ticketStatusGroupLabelKey,
} from '@nbh/shared';
import { type QueueView, emptyStateKey, hasChips } from './queueView';

/**
 * The empty state, naming every active filter and clearing them in one tap.
 *
 * Tabs and chips compose and chips persist across tabs, so empty results are common
 * and usually correct — New + Mine + Urgent will often be empty. A bare "No jobs" here
 * reads as a broken app, and would be the most likely support complaint arising from
 * the tabs. "No urgent jobs assigned to you in New. [Clear filters]" is an answer.
 */
export function QueueEmptyState({ view, onClear }: { view: QueueView; onClear: () => void }) {
  const { t } = useTranslation();
  const tab = t(ticketStatusGroupLabelKey[view.group]);
  const priority =
    view.chips.priority === null
      ? undefined
      : t(ticketPriorityLabelKey[view.chips.priority]).toLocaleLowerCase();

  return (
    <div className="flex flex-col items-start gap-3 p-4">
      <p className="text-sm text-slate-300">{t(emptyStateKey(view.chips), { tab, priority })}</p>
      {view.group === TicketStatusGroup.Closed && (
        <p className="text-xs text-slate-500">
          {t('queue.empty.closedHint', {
            range: t(`queue.range.describe.${view.range.preset}`, {
              from: view.range.from,
              to: view.range.to,
            }),
          })}
        </p>
      )}
      {hasChips(view.chips) && (
        <button
          type="button"
          onClick={onClear}
          className="rounded-lg border border-slate-600 bg-transparent px-4 text-sm font-medium text-slate-100"
        >
          {t('queue.clearFilters')}
        </button>
      )}
    </div>
  );
}

/**
 * Said plainly when a tab fills its page: there is no second page to reach for. On
 * Closed the remedy is a narrower range; on a live tab, a chip.
 */
export function TruncatedNotice({ group, limit }: { group: TicketStatusGroup; limit: number }) {
  const { t } = useTranslation();
  const key = group === TicketStatusGroup.Closed ? 'queue.truncated.closed' : 'queue.truncated.live';

  return (
    <p
      role="status"
      className="border-b border-slate-800 bg-amber-950/30 px-4 py-2 text-xs text-amber-200"
    >
      {t(key, { limit })}
    </p>
  );
}
